/**
 * Escape-to-close and click-outside for the desktop nav dropdowns.
 *
 * The panels are `<details name="header-menu">`, so the browser already makes
 * them mutually exclusive and keyboard-operable with no JavaScript, and that
 * stays true if this module never loads. What the native element does not give
 * you is a way out other than clicking the same summary again: a shopper who
 * opens a panel and then clicks the page, or tabs past it, leaves it hanging
 * open over the content.
 *
 * No custom element and no Component subclass: there is nothing to render and
 * no state to own, only three listeners over markup the server already sent.
 */
const header = document.querySelector('.header');

if (header) {
  /** @returns {HTMLDetailsElement | null} */
  const openPanel = () => header.querySelector('details[name="header-menu"][open]');

  /**
   * @param {boolean} returnFocus - true when the shopper asked to close, so
   *   focus goes back to the summary rather than being dropped at the top of
   *   the document. False when they clicked elsewhere and focus belongs there.
   */
  const close = (returnFocus) => {
    const panel = openPanel();
    if (!panel) return;
    panel.open = false;
    if (returnFocus) panel.querySelector('summary')?.focus();
  };

  document.addEventListener('keydown', (event) => {
    // Only claim Escape when a panel is actually open, so a dialog above us
    // keeps handling its own.
    if (event.key === 'Escape' && openPanel()) close(true);
  });

  // pointerdown rather than click: it fires before focus moves, so the panel is
  // gone by the time the thing underneath is activated, and it covers a tap on
  // something that cannot take focus at all.
  document.addEventListener('pointerdown', (event) => {
    const panel = openPanel();
    if (panel && event.target instanceof Node && !panel.contains(event.target)) close(false);
  });

  // Tabbing out of the panel closes it too — relatedTarget is where focus is
  // going, and null means it left the document entirely.
  header.addEventListener('focusout', (event) => {
    const panel = openPanel();
    if (!panel) return;
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !panel.contains(next)) close(false);
  });
}

/**
 * The home page header floats on the hero with no background of its own. Once
 * the page has moved at all, the photograph is no longer behind it, so it takes
 * its ground back.
 *
 * A scroll position, not an observer on the hero: the header must also solidify
 * on a home page whose first section is not the hero, and `scrollY` is true
 * regardless of what is under it. Read inside rAF so a fast flick does not
 * force layout on every frame of the scroll.
 */
if (header?.classList.contains('header--overlay')) {
  let queued = false;

  const sync = () => {
    queued = false;
    header.classList.toggle('header--solid', window.scrollY > 8);
  };

  sync();
  window.addEventListener(
    'scroll',
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    },
    { passive: true }
  );
}
